import type { ReactNode } from 'react'
import CalendarDatePicker from './CalendarDatePicker'
import CalendarViewSwitcher, { type CalendarView } from './CalendarViewSwitcher'

/**
 * The control row over any schedule: which slice, which date, and what you can
 * do to it (D-359).
 *
 * **One component because it is one idea.** The teaching half of Schedule had a
 * date picker on its title, a dropdown switcher and arrows; the children's half
 * had a segmented toggle, plain arrows and *no way to pick a date at all* — one
 * tap apart, inside a single destination, neither aware of the other. The owner
 * asked whether they should be aligned. They should, and the way to make that
 * stay true is to have one implementation rather than two that agree today.
 *
 * **It takes the better half of each rather than making one copy the other.**
 * The date picker is the piece the children's half was simply missing — with
 * arrows alone, a parent planning around a recital in November taps eight
 * times. The segmented switcher is the better control on a phone, and it now
 * belongs to `CalendarViewSwitcher` at three options, so this component does
 * not choose it.
 *
 * **Nothing here owns any state.** The caller says where the anchor lives — the
 * address bar for both halves of Schedule (D-360), a route parameter for the
 * master calendar. That is what lets one half hand its date to the other.
 */
export default function ScheduleHeader({
  eyebrow,
  above,
  label,
  subtitle,
  anchor,
  view,
  onPick,
  onView,
  onStep,
  onToday,
  actions,
  hideTeachers,
}: {
  /** Small caps line over the title, e.g. "Master calendar". */
  eyebrow?: string
  /** Anything that belongs above the title, such as a way back to the week. */
  above?: ReactNode
  /** The rendered title, e.g. "August 3-9, 2026". Doubles as the picker. */
  label: string
  /** A line under the title — counts, and why the week looks empty. */
  subtitle?: ReactNode
  anchor: string
  view: CalendarView
  onPick: (date: string) => void
  /**
   * What a view change does. **Required** (D-395).
   *
   * **It was optional, and omitting it navigated to `/calendar/…`** — routes
   * gated on `ViewAllSchedules`, the capability an embed exists to work around
   * (D-312). So a screen that embedded a calendar and forgot this prop offered
   * a parent three controls landing on *"Not your page"*, and nothing at the
   * call site said so.
   *
   * The studio's own calendar screens now pass a handler that navigates, which
   * is what they were getting by omission — the difference is that it is
   * written where the route is known and where the capability is already
   * guaranteed, rather than inside a control that renders three buttons.
   */
  onView: (view: CalendarView) => void
  onStep: (direction: 1 | -1) => void
  onToday: () => void
  /**
   * Things you do *to this view*, left of the switcher (D-321) — *Book a room*
   * and *I am out* for a teacher, *Report an absence* and *Ask for a private*
   * for a parent. The same slot, because they are the same kind of thing.
   */
  actions?: ReactNode
  hideTeachers?: boolean
}) {
  // "By teacher" is a day, so it steps and picks like one.
  const unit = view === 'teachers' ? 'day' : view

  const step =
    'rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 hover:border-ink-3'

  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {above}

        {eyebrow && (
          <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">{eyebrow}</p>
        )}

        <CalendarDatePicker label={label} anchor={anchor} view={view} onPick={onPick} />

        {subtitle}
      </div>

      {/*
        `min-w-0` because one of these actions opens a form containing a
        horizontally scrolling grid. A flex item's `min-width` defaults to
        `auto`, so an intrinsic minimum inside it propagates straight up
        through every unfloored ancestor — measured at 390px, this row and the
        form both rendered 570px and the page scrolled sideways by 200. The
        `overflow-x-auto` on the grid cannot help while an ancestor refuses to
        be narrower than its content. Same family as D-229.
      */}
      <nav className="flex min-w-0 flex-wrap items-center gap-2">
        {actions}

        <CalendarViewSwitcher
          hideTeachers={hideTeachers}
          current={view}
          onSelect={onView}
        />

        {/*
          <b>The pager is one thing and wraps as one thing</b> (D-517).

          Reported off a phone: at 390px the row ran out of width after
          <i>Today</i>, so `&rarr;` alone dropped to the next line and sat under
          the controls with nothing beside it. The two arrows belong either side
          of the button — the note below is about not letting them move relative
          to it, and a wrap moves one of them by a whole line.

          The divider comes inside the group so it travels with the pager
          instead of being left dangling at the end of the row above — and it
          is dropped on a phone, where the pager is on its own line and a rule
          separating it from nothing reads as a stray tick. It is decorative
          and already `aria-hidden`, so nothing is lost by its absence.
        */}
        <span className="flex shrink-0 items-center gap-2">
          <span className="mx-1 hidden h-6 w-px bg-line sm:block" aria-hidden="true" />

          <button type="button" onClick={() => onStep(-1)} aria-label={`Previous ${unit}`} className={step}>
            &larr;
          </button>

          {/*
          Always rendered, never hidden when it is already today.
          <b>Deliberately the teaching half's behaviour rather than the
          children's</b>, which showed *Back to today* only when it would do
          something (D-359).

          The argument for hiding it is D-257 — a control that changes nothing.
          The argument against is that this one sits *between the two arrows*,
          so hiding it moves them, and a row that reshuffles itself as you page
          is worse than a button that is occasionally a no-op. The parent half
          gains a control rather than losing one.
          */}
          <button
            type="button"
            onClick={onToday}
            className="rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent"
          >
            {/* "Today" is not a month. The wording follows the unit rather than
                flattening to one word for all three. */}
            {view === 'month' ? 'This month' : 'Today'}
          </button>

          <button type="button" onClick={() => onStep(1)} aria-label={`Next ${unit}`} className={step}>
            &rarr;
          </button>
        </span>
      </nav>
    </header>
  )
}
